﻿import { MILL_1 } from "@/setup/mill-config/mill-config";
import { MESApiService, spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { Section, SectionGap } from "../models/Section";

export const upsertSection = async (params: {
	section: Section;
	defaultAreaId?: string | number | null;
}) => {
	const apiService = new MESApiService();

	const isM1 = Number(params.defaultAreaId) === MILL_1.id;

	const parameters: spParameter[] = [
		{ name: "SectionId", value: params.section.SectionId },
		{ name: "SectionName", value: params.section.SectionName },
		{ name: "SectionFamilyId", value: params.section.SectionFamilyId },
		{ name: "SortOrder", value: params.section.SortOrder },
		{ name: "BilletSize", value: params.section.BilletSize },
		{ name: "Description", value: params.section.Description },
		{ name: "TonsPerHour", value: params.section.TonsPerHour },
		{ name: "TargetTPH", value: params.section.TargetTPH },
		{ name: "MillYield", value: params.section.MillYield || null },
		{
			name: "FinishedYield",
			value: params.section.FinishedYield || null,
		},
	];

	// LBsPerFoot only exists on M2.Sections / M2.UpsertSection
	if (!isM1) {
		parameters.push({
			name: "LBsPerFoot",
			value: params.section.LBsPerFoot,
		});
	}

	const resp = await apiService.callV2(
		`${isM1 ? "[M1].[UpsertSection]" : "[M2].[UpsertSection]"}`,
		parameters,
	);

	if (resp.ok) {
		return get(resp, "data.returnValue", null) as number;
	} else {
		throw new Error(resp.message || "Error upserting section");
	}
};

export const deleteSection = async (params: {
	sectionId: number | null;
	defaultAreaId?: string | number | null;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "SectionId", value: params.sectionId },
	];

	const resp = await apiService.callV2(
		`${Number(params.defaultAreaId) === MILL_1.id ? "[M1].[DeleteSection]" : "[M2].[DeleteSection]"}`,
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error deleting section");
	}
};

export const getSectionGaps = async (
	params: { sectionId?: number | null; defaultAreaId?: string | number | null },
	signal?: AbortSignal,
) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "SectionId", value: params.sectionId ?? 0 },
	];

	const resp = await apiService.callV2(
		`${Number(params.defaultAreaId) === MILL_1.id ? "[M1].[GetSectionGaps]" : "[M2].[GetSectionGaps]"}`,
		parameters,
		signal,
	);

	if (resp.ok) {
		return get(resp, "data.tables[0].rows", []) as SectionGap[];
	} else {
		throw new Error(resp.message || "Error fetching section gaps");
	}
};

export const upsertSectionGap = async (params: {
	sectionGap: SectionGap;
	defaultAreaId?: string | number | null;
}) => {
	const apiService = new MESApiService();

	const parameters: spParameter[] = [
		{ name: "SectionId", value: params.sectionGap.SectionId },
		{ name: "StandId", value: params.sectionGap.StandId },
		{ name: "SetpointGap", value: Number(params.sectionGap.SetpointGap) },
		{ name: "Comments", value: params.sectionGap.Comments },
	];

	const resp = await apiService.callV2(
		`${Number(params.defaultAreaId) === MILL_1.id ? "[M1].[UpsertSectionGap]" : "[M2].[UpsertSectionGap]"}`,
		parameters,
	);

	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching section gaps");
	}
};
